// @LTG-ID: data/journals/customer-concentration-selling-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 13 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "customer-concentration-selling-business",
    FRONTMATTER: {
        number: "50",
        section: "Value Drivers",
        sectionHref: "/journals#value-drivers",
        sectionAnchor: "value-drivers",
        title: "Customer Concentration: How Much Is Too Much When Selling a Business?",
        deck: "Customer concentration becomes a serious valuation issue when any single customer approaches 15 to 20 percent of revenue, a common rule of thumb among brokers and lenders.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-04-03",
        publishedHuman: "April 3, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 456,
        timeRequired: "PT3M",
        keywords: "customer concentration selling a business, customer concentration risk, one customer too much revenue, customer concentration threshold",
        articleSection: "Value Drivers",
        articleBodyPreview: "Customer concentration becomes a serious valuation issue when any single customer approaches 15 to 20 percent of revenue, a common rule of thumb among brokers and lenders. Above that, buyers discount the price, restructure deals with earnouts, or walk away, because losing one relationship could impair the business they just bought. Your biggest customer is your biggest asset right up until you try to sell, when it becomes your biggest liability. Few value drivers flip meaning so completely at th",
        complianceNote: "This article is educational and does not constitute financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-long-does-it-take-to-sell-a-business",
        "business-value-drivers",
        "recurring-revenue-business-value"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Customer Concentration: How Much Is Too Much? · LTG",
        canonical: "https://letstalkgrowth.com/journals/customer-concentration-selling-business",
        ogImage: "/journals/og/customer-concentration-selling-business.jpg",
    },
    BODY_MARKDOWN: `Customer concentration becomes a serious valuation issue when any single customer approaches 15 to 20 percent of revenue, a common rule of thumb among brokers and lenders. Above that, buyers discount the price, restructure deals with earnouts, or walk away, because losing one relationship could impair the business they just bought.

Your biggest customer is your biggest asset right up until you try to sell, when it becomes your biggest liability. Few value drivers flip meaning so completely at the transaction table.

## Why buyers fear what owners prize

An owner sees a decade-long relationship built on trust and performance. A buyer sees a single point of failure they did not build and may not hold. The relationship's history belongs to the seller; its risk transfers to the buyer. Lenders see it the same way: acquisition financing gets harder when repayment depends on one account's loyalty to an owner who just left.

## The rules of thumb buyers actually use

There is no law of concentration, but broker and lender practice has settled into recognizable zones. When no customer exceeds roughly a tenth of revenue, concentration rarely enters the conversation. As a single customer approaches 15 to 20 percent, buyers begin asking pointed questions and pricing the risk. When one customer represents a quarter, a third, or more of revenue, expect one or more of the following: a lower price, an earnout tied to that customer's retention, a holdback, or a smaller pool of willing buyers. In severe cases, concentration alone ends conversations before price is even discussed.

The percentages are market shorthand, not statutes, and context moderates them. A concentrated customer bound by a multi-year assignable contract is a different risk than one on a handshake. Government or blue-chip customers with formal procurement processes read differently than a single private account. And concentration in an industry where big anchor accounts are structurally normal, some contract manufacturing, for example, is judged against that industry's own norms.

## If you are concentrated now

Fixing concentration takes years, not weeks, which is exactly why it belongs at the top of a pre-exit work list.

- **Grow around the anchor.** The healthiest fix is rarely shrinking the big customer; it is growing everyone else until the anchor's share falls naturally.
- **Put paper on the relationship.** Convert informal arrangements into term contracts with assignment provisions. A contracted anchor is a partially de-risked anchor.
- **Spread the relationship.** Multiple contacts on both sides, documented service history, and institutional rather than personal ties all make the account more transferable.
- **Expect the structure conversation.** If concentration remains at sale time, earnouts and retention-linked terms are the market's standard compromise. Sellers who understand this negotiate those terms rather than being ambushed by them.`,
    FAQ: [
        { question: "What percentage of revenue from one customer is acceptable to buyers?", answer: "Common broker and lender rules of thumb treat single customers below roughly 10 percent of revenue as comfortable and begin applying scrutiny around 15 to 20 percent. These are market conventions rather than fixed rules, and contracts and customer quality shift the judgment." },
        { question: "Will customer concentration stop my business from selling?", answer: "Severe concentration can, particularly without contracts. More often it reshapes the deal: lower price, earnouts tied to customer retention, or holdbacks. Reducing concentration before a sale expands both the buyer pool and the terms available." },
        { question: "Does concentration matter with a signed long-term contract?", answer: "A multi-year assignable contract meaningfully reduces the risk, and buyers price it accordingly. It rarely eliminates the discount entirely, since renewal risk transfers to the new owner." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Value Drivers",
            title: "The Value Drivers That Determine What Buyers Will Pay for Your Business",
            meta: "4 min · Brief 48",
            to: "/journals/business-value-drivers",
        },
        {
            tag: "Value Drivers",
            title: "Recurring Revenue: Why Buyers Pay More for Businesses That Bill Every Month",
            meta: "3 min · Brief 49",
            to: "/journals/recurring-revenue-business-value",
        },
    ],
};

export default BRIEF;
